/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import * as AbsenceActions from '@absences/store/absences.actions';
import { AbsenceOwner } from '@absences/store/absence-owner';
import { getAbsenceClasses, getAbsences, isAbsenceActionPending } from '@absences/store/absences.selectors';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { State } from '@core/store';
import { DriversService } from '@drivers/services/drivers.service';
import { Store } from '@ngrx/store';
import { Absence, AbsenceClass } from '@shared/models';
import { VehiclesService } from '@vehicles/services/vehicles.service';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-absences',
    templateUrl: './absences.component.html',
    styleUrls: ['./absences.component.scss'],
    standalone: false
})
export class AbsencesComponent implements OnInit, OnDestroy {
  readonly tableColumns = ['startDate', 'endDate', 'class', 'actions'];
  ownerLabel$: Observable<string>;
  absences$: Observable<Absence[]>;
  absenceClasses$: Observable<AbsenceClass[]>;
  actionPending$: Observable<boolean>;
  routeSubscription: Subscription;

  constructor(
    private store: Store<State>,
    private route: ActivatedRoute,
    private driversService: DriversService,
    private vehiclesService: VehiclesService
  ) { }

  ngOnInit(): void {
    // Dieselbe Seite haengt unter /drivers/:driverId und unter /vehicles/:vehicleId.
    // ponytail: Fahrer bzw. Fahrzeug werden hier direkt ueber den Service geladen statt
    // ueber den jeweiligen Store - VehicleActions.LoadVehicle oeffnet den Bearbeiten-
    // Dialog und taugt dafuer nicht. Gebraucht wird ohnehin nur die IRI des Ziels plus
    // ein Name fuer die Kopfzeile.
    const owner$ = this.route.params.pipe(
      switchMap(params => params.vehicleId
        ? this.vehiclesService.getVehicle(params.vehicleId).pipe(
          map(vehicle => ({
            owner: { property: 'vehicle', id: vehicle['@id'] } as AbsenceOwner,
            label: vehicle.licensePlate
          }))
        )
        : this.driversService.getDriver(params.driverId).pipe(
          map(driver => ({
            owner: { property: 'driver', id: driver['@id'] } as AbsenceOwner,
            label: `${driver.firstName} ${driver.lastName}`
          }))
        )
      ),
      shareReplay(1)
    );

    this.routeSubscription = owner$.pipe(
      tap(({ owner }) => this.store.dispatch(new AbsenceActions.LoadAbsences(owner)))
    ).subscribe();

    this.ownerLabel$ = owner$.pipe(map(({ label }) => label));
    this.absences$ = this.store.select(getAbsences).pipe(
      map(absences => {
        const years = [];
        const allYears = absences
          .map(absence => new Date(absence.endDate).getFullYear())
          .filter((value, index, array) => array.indexOf(value) === index);

        for (const year of allYears) {
          years.push([year, absences.filter(absence => new Date(absence.endDate).getFullYear() === year)]);
        }

        return years as any;
      })
    );
    this.absenceClasses$ = this.store.select(getAbsenceClasses);
    this.actionPending$ = this.store.select(isAbsenceActionPending);
  }

  editAbsence(absence: Absence = { } as Absence): void {
    this.store.dispatch(new AbsenceActions.EditAbsence(absence));
  }

  deleteAbsence(id: string): void {
    this.store.dispatch(new AbsenceActions.DeleteAbsence(id));
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }
}
